import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { activityApi, usersApi } from '../../services/api';
import Avatar from '../shared/Avatar';
import EmptyState from '../shared/EmptyState';
import { formatTimeAgo, formatActionLabel } from '../../utils/formatters';
import clsx from 'clsx';

const ENTITY_COLORS: Record<string, string> = {
  task: 'bg-blue-100 text-blue-600',
  interview: 'bg-green-100 text-green-600',
  calling: 'bg-purple-100 text-purple-600',
  meeting: 'bg-orange-100 text-orange-600',
};

const ENTITY_LABELS: Record<string, string> = {
  task: 'Tarefa',
  interview: 'Entrevista',
  calling: 'Chamado',
  meeting: 'Reunião',
};

export default function ActivityLogPage() {
  const [userId, setUserId] = useState('all');
  const [entityType, setEntityType] = useState('all');

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    staleTime: 60_000,
  });

  const users = usersData?.data || [];

  const { data, isLoading } = useQuery({
    queryKey: ['activity', { user_id: userId, entity_type: entityType }],
    queryFn: () =>
      activityApi.list({
        user_id: userId !== 'all' ? userId : undefined,
        entity_type: entityType !== 'all' ? entityType : undefined,
        limit: 50,
      }),
    refetchInterval: 30_000,
  });

  const logs = (data?.data || []) as any[];

  return (
    <div>
      {/* Filters */}
      <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
        <select
          value={userId}
          onChange={e => setUserId(e.target.value)}
          className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 flex-shrink-0 bg-white"
        >
          <option value="all">Todos os usuários</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>{u.name.split(' ')[0]}</option>
          ))}
        </select>

        <select
          value={entityType}
          onChange={e => setEntityType(e.target.value)}
          className="text-xs border border-gray-200 rounded-lg px-2 py-1.5 text-gray-600 flex-shrink-0 bg-white"
        >
          <option value="all">Todos os tipos</option>
          {Object.entries(ENTITY_LABELS).map(([t, l]) => (
            <option key={t} value={t}>{l}</option>
          ))}
        </select>
      </div>

      {/* Feed */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => <div key={i} className="card animate-pulse h-16" />)}
        </div>
      ) : logs.length === 0 ? (
        <EmptyState
          icon={Activity}
          title="Nenhuma atividade registrada"
          description="As ações realizadas no sistema aparecerão aqui"
        />
      ) : (
        <div className="space-y-2">
          {logs.map((log) => (
            <div key={log.id} className="card py-3">
              <div className="flex items-start gap-3">
                {log.user ? (
                  <Avatar
                    name={log.user.name}
                    photoUrl={log.user.photo_url}
                    size="sm"
                  />
                ) : (
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex-shrink-0 flex items-center justify-center">
                    <Activity size={14} className="text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm text-gray-700 leading-snug">
                      <span className="font-medium">
                        {log.user?.name?.split(' ')[0] || 'Sistema'}
                      </span>
                      {' '}{formatActionLabel(log.action)}
                    </p>
                    <span className={clsx(
                      'text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0',
                      ENTITY_COLORS[log.entity_type] || 'bg-gray-100 text-gray-600'
                    )}>
                      {ENTITY_LABELS[log.entity_type] || log.entity_type}
                    </span>
                  </div>

                  {/* Details snippet */}
                  {log.details && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">
                      {log.details.title || log.details.person_name || log.details.calling_name
                        ? `"${log.details.title || log.details.person_name || log.details.calling_name}"`
                        : null}
                    </p>
                  )}

                  <p className="text-xs text-gray-400 mt-1">
                    {formatTimeAgo(log.created_at)}
                  </p>
                </div>
              </div>
            </div>
          ))}

          {(data?.total || 0) > 50 && (
            <p className="text-xs text-gray-400 text-center py-2">
              Mostrando as 50 atividades mais recentes
            </p>
          )}
        </div>
      )}
    </div>
  );
}
